import React, { useEffect, useMemo, useRef, useState } from 'react';

const groupBy = (items, getKey) => {
  const map = new Map();
  items.forEach((item) => {
    const key = getKey(item) || 'Other';
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(item);
  });
  return map;
};

export default function RecipientMultiSelect({
  label = 'Share with',
  placeholder = 'Search staff by name or role…',
  options = [],
  selectedIds = [],
  onChange,
  quickPicks = [],
  error = '',
  helperText = '',
  recommendedId = '',
  allowNone = false
}) {
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const [announcement, setAnnouncement] = useState('');

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedQuery(query.trim().toLowerCase()), 200);
    return () => window.clearTimeout(handle);
  }, [query]);

  const filtered = useMemo(() => {
    if (!debouncedQuery) return options;
    return options.filter((option) => {
      const haystack = `${option.name} ${option.meta || ''}`.toLowerCase();
      return haystack.includes(debouncedQuery);
    });
  }, [options, debouncedQuery]);

  const grouped = useMemo(
    () => groupBy(filtered, (item) => item.group || item.meta || 'Other'),
    [filtered]
  );

  const flatOptions = useMemo(() => {
    const list = [];
    grouped.forEach((items, group) => {
      items.forEach((item) => {
        list.push({ ...item, group });
      });
    });
    return list;
  }, [grouped]);

  const selectedOptions = useMemo(
    () => options.filter((option) => selectedIds.includes(option.id)),
    [options, selectedIds]
  );

  const listboxId = 'recipient-listbox';

  useEffect(() => {
    if (!selectedOptions.length) {
      setAnnouncement('No recipients selected.');
      return;
    }
    const names = selectedOptions.map((item) => item.name).join(', ');
    setAnnouncement(`${selectedOptions.length} recipient${selectedOptions.length === 1 ? '' : 's'} selected. ${names}.`);
  }, [selectedOptions]);

  const updateSelection = (nextIds) => {
    if (!allowNone && nextIds.length === 0) return;
    onChange(nextIds);
  };

  const toggleOption = (id) => {
    const isSelected = selectedIds.includes(id);
    const next = isSelected ? selectedIds.filter((entry) => entry !== id) : [...selectedIds, id];
    updateSelection(next);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setHighlighted((prev) => Math.min(prev + 1, flatOptions.length - 1));
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlighted((prev) => Math.max(prev - 1, 0));
    }
    if (event.key === 'Enter') {
      if (!open) return;
      event.preventDefault();
      const option = flatOptions[highlighted];
      if (option && !option.disabled) {
        toggleOption(option.id);
      }
    }
    if (event.key === 'Escape') {
      setOpen(false);
    }
    if (event.key === 'Backspace' && !query && selectedIds.length) {
      updateSelection(selectedIds.slice(0, -1));
    }
  };

  const handleBlur = (event) => {
    if (listRef.current && listRef.current.contains(event.relatedTarget)) return;
    setOpen(false);
  };

  return (
    <div className="space-y-2">
      <label className="sp-label">{label}</label>
      {helperText && <div className="text-xs sp-muted">{helperText}</div>}
      <div className="sp-combobox">
        <input
          ref={inputRef}
          className={`sp-input w-full ${error ? 'sp-input--error' : ''}`}
          type="text"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-haspopup="listbox"
          aria-activedescendant={open && flatOptions[highlighted] ? `recipient-${flatOptions[highlighted].id}` : undefined}
          placeholder={placeholder}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
            setHighlighted(0);
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => setOpen(true)}
          onBlur={handleBlur}
        />
        {open && (
          <div className="sp-combobox-list" role="listbox" id={listboxId} ref={listRef}>
            {quickPicks.length > 0 && (
              <div className="sp-combobox-quick">
                {quickPicks.map((pick) => (
                  <button
                    key={pick.id}
                    type="button"
                    className="sp-button sp-button--ghost"
                    onClick={() => {
                      pick.onSelect();
                      inputRef.current?.focus();
                    }}
                  >
                    {pick.label}
                  </button>
                ))}
              </div>
            )}
            {flatOptions.length === 0 && (
              <div className="sp-combobox-empty">No staff found.</div>
            )}
            {Array.from(grouped.entries()).map(([group, items]) => (
              <div key={group} className="sp-combobox-group" role="presentation">
                <div className="sp-combobox-heading">{group}</div>
                {items.map((option) => {
                  const isSelected = selectedIds.includes(option.id);
                  return (
                    <div
                      key={option.id}
                      id={`recipient-${option.id}`}
                      role="option"
                      aria-selected={isSelected}
                      className={`sp-combobox-option ${isSelected ? 'is-selected' : ''} ${option.disabled ? 'is-disabled' : ''}`}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => {
                        if (option.disabled) return;
                        toggleOption(option.id);
                        inputRef.current?.focus();
                      }}
                    >
                      <div>
                        <div className="sp-combobox-name">
                          {option.name}
                          {option.id === recommendedId && (
                            <span className="sp-badge sp-badge--subtle ml-2">Recommended</span>
                          )}
                        </div>
                        {option.meta && <div className="sp-combobox-meta">{option.meta}</div>}
                        {option.disabled && option.disabledReason && (
                          <div className="sp-combobox-meta">{option.disabledReason}</div>
                        )}
                      </div>
                      <div>{isSelected ? 'Selected' : ''}</div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="sp-chip-list" aria-live="polite">
        {selectedOptions.length === 0 && (
          <div className="text-xs sp-warning-text">No recipients selected.</div>
        )}
        {selectedOptions.map((option) => (
          <span key={option.id} className="sp-chip">
            {option.name}
            {option.meta && <span className="sp-chip-meta">· {option.meta}</span>}
            <button
              type="button"
              className="sp-chip-remove"
              aria-label={`Remove ${option.name}`}
              onClick={() => toggleOption(option.id)}
            >
              ×
            </button>
          </span>
        ))}
      </div>
      {error && <div className="text-xs sp-warning-text">{error}</div>}
      <div className="sr-only" aria-live="polite">{announcement}</div>
    </div>
  );
}
